// ════════════════════════════════════════════════════════════════════
// TERROR — HOW A SOLREND ACTUALLY ENDS
// ────────────────────────────────────────────────────────────────────
// ONE question, asked from both drivers: "seat W just won against seat
// L — does the MATCH end, or does one side simply get knocked out?"
//
// WHY THIS FILE EXISTS AT ALL. That question had exactly one answer in
// the whole project and it lived inside Shatterfield.js as a
// `useCallback` (`finishOrEliminate`) — i.e. inside the renderer, bound
// to React state and to `showToast`. Every win condition the RENDERER
// owns went through it and knocked out one seat correctly in a 3-4p FFA
// or a 2v2. Every win condition the ENGINE owns — prophecyEngine's
// automatic Prophecy fire being the one that existed — called
// gameManager's raw `endMatch` instead, which sets `status: "finished"`
// on the WHOLE TABLE. In a four-seat FFA that meant an auto-fired
// Prophecy ended the game for two players who were never even involved.
//
// So the answer is hoisted here, where an engine file may import it and
// the renderer may keep its toast. It is deliberately NOT in
// gameManager.js: gameManager is the Firebase touchpoint and importing
// matchSeats into it would point the dependency arrow backwards.
//
// ⚠ THIS FILE IS IN THE HEADLESS ENGINE CLOSURE (tools/harness/build.mjs
// mirrors it). Keep it JSX-free and React-free or the harness build
// refuses, loudly, by name.
// ════════════════════════════════════════════════════════════════════

import { updateMatch, endMatch as endMatchRaw } from "./gameManager";
import {
  getTargetIdx, teamOf, teammatesOf, matchIsDecided,
  survivingTeamsOrSeats, getNextActiveSeat,
} from "./matchSeats";

// ── THE HAND-OFF, INJECTED ──────────────────────────────────────────
// When a mid-turn elimination knocks out the seat that was HOLDING THE
// CHAIR, the chair moves — and moving the chair is src/turnEngine.js's
// entire job: `turn++`, R93's clock, the outgoing seat's Anima ledger.
// This file used to do it with four hand-written lines that did none of
// those things, so every mid-turn elimination in a 3-4p FFA handed a
// fresh seat a FULL half-turn that R93 never counted. Over a long FFA
// the clock under-counted by one per knockout, which is a duel that
// runs past its own limit.
//
// It cannot simply `import { handOffHalfTurn } from "./turnEngine"`:
// turnEngine imports prophecyEngine, prophecyEngine imports this file,
// and closing that loop would also give handOffHalfTurn a route back
// into itself through checkProphecies. So turnEngine INJECTS itself on
// load — the same shape ruleGate uses for its effective-Lyf resolver —
// and the inline reseat below stays as the fallback for any consumer
// that reaches this file without turnEngine ever being loaded.
let _handOffHalfTurn = null;
export const setHandOffHalfTurn = (fn) => { _handOffHalfTurn = fn; };

// ── THE CO-WINNER RESOLVER, INJECTED (R98, 2026-08-25) ──────────────
// caP'n: *"prophecies do not prevent wins merely grant them, the other
// player can still win naturally or by their own prophecy"* — so the
// instant a Solrend is decided, any OTHER seat sitting on a fulfilled,
// unspent, affordable Prophecy wins TOO.
//
// The question is prophecyEngine's ("is this seat's Prophecy true?");
// the moment is this file's ("the Solrend just ended"). prophecyEngine
// already imports this module, so the arrow cannot be reversed — the
// resolver is injected exactly the way turnEngine injects its hand-off
// above, and for exactly the same reason.
//
// Absent = no co-winners, which is the pre-R98 behaviour and the right
// fallback for any consumer that never loads prophecyEngine.
let _coWinners = null;
export const setCoWinnerResolver = (fn) => { _coWinners = fn; };

// ── finishOrEliminate, as an engine function ────────────────────────
// `match` is passed BY ARGUMENT and is never read from anywhere else:
// a React caller hands it `matchRef.current`, the NPC brain hands it
// whatever snapshot it is holding. The function itself has no opinion
// about who is driving.
//
// Returns a result the caller can narrate:
//   { ended, winner, runnerUp, eliminatedSeats }
// `ended` true  — the match is over; `winner`/`runnerUp` were archived.
// `ended` false — the losing side was knocked out and play continues;
//                 `eliminatedSeats` says who left the table, so the
//                 renderer can toast it and a headless driver can not.
//
// ⚠ endMatchRaw, never gameManager's escalation-aware wrapper. A side
// that has been knocked out does not get handed a boss Second Wind on
// the way out — that was already true of the renderer's copy (see the
// note at Shatterfield's handleActivateProphecy) and is preserved
// exactly here.
export async function finishOrEliminateSeat(code, match, winnerIdx, explicitLoserIdx = null) {
  const loserIdx = explicitLoserIdx != null ? explicitLoserIdx : getTargetIdx(match, winnerIdx);
  const loserTeam = teamOf(match, loserIdx);
  const eliminatedSeats = loserTeam != null ? [...teammatesOf(match, loserIdx), loserIdx] : [loserIdx];
  const projectedPlayers = { ...match.players };
  eliminatedSeats.forEach((i) => { projectedPlayers[i] = { ...projectedPlayers[i], eliminated: true }; });
  const projected = { ...match, players: projectedPlayers };

  if (matchIsDecided(projected)) {
    const survivors = survivingTeamsOrSeats(projected);
    // ⚠ THE EMPTY-SURVIVORS FALLBACK IS A LAST RESORT, NOT A RULE.
    // `survivingTeamsOrSeats` runs on matchSeats.isEliminated, which is
    // `eliminated || effective Anima <= 0`. So a board where BOTH sides
    // are non-living — the loser knocked out here, the winner sitting
    // on 0 Anima in some async gap — produces NO survivors and this
    // line hands the duel to `winnerIdx`, i.e. to a corpse.
    //
    // That was reachable exactly once, through prophecyEngine, whose
    // liveness filter was the only one in the project that did not ask
    // about Anima. It is sealed AT THE SOURCE now (see the banner on
    // `seats` in checkProphecies) rather than papered over here,
    // because every other caller of this function reaches it BECAUSE a
    // seat just hit 0 — "the winner is the party that won this
    // exchange" is the only answer available and is the right one. The
    // note is here so the next reader knows this branch is a floor
    // under an incoherent board, not the normal path.
    const winner = survivors.length ? survivors[0] : (loserTeam != null ? teamOf(match, winnerIdx) : winnerIdx);
    // The side JUST knocked out to decide the match is, definitionally,
    // whoever placed 2nd — see endMatch's own comment for why a
    // Tournament of Kings payout needs this.
    const runnerUp = loserTeam != null ? loserTeam : loserIdx;

    // ── R98 · WHO ELSE WON ─────────────────────────────────────────
    // ⚠ ASKED AGAINST `match`, NOT `projected`, AND THE DIFFERENCE IS
    // THE RULING. `projected` is the board with the losing side already
    // flagged out; asking it (and then filtering those seats away) made
    // R98 unreachable in 1v1, because in a duel the only other player
    // IS the seat that just took the finishing blow. caP'n's words are
    // *"both players win"* — so the seat knocked out BY THIS BLOW is
    // exactly the seat the ruling is about. coWinningSeats still
    // refuses anyone who was already out before this exchange.
    let coWins = [];
    if (typeof _coWinners === "function") {
      try { coWins = _coWinners(match, winner) || []; }
      catch (e) { console.error("[R98] co-winner resolver threw:", e && e.message); }
    }
    coWins = coWins.filter((c) => c && c.id !== winner);
    const winners = [winner, ...coWins.map((c) => c.id).filter((id, i, a) => a.indexOf(id) === i && id !== winner)];

    // R99 — a Prophecy is spent when it resolves, and co-winning IS a
    // resolution. Stamped before the duel closes so the archive shows
    // the card as used; batched into one write with the co-win record
    // so a reader can never see half of it.
    if (coWins.length) {
      const stamp = {};
      coWins.forEach((c) => { stamp[`players/${c.seat}/prophecySpent`] = c.prophecyId; });
      stamp.coWonByProphecy = coWins.map((c) => `${c.seat}:${c.prophecyId}`).join(",");
      try { await updateMatch(code, stamp); }
      catch (e) { console.error("[R98] co-winner stamp failed:", e && e.message); }
    }

    await endMatchRaw(code, winner, runnerUp, winners);
    return { ended: true, winner, winners, runnerUp, eliminatedSeats, coWinners: coWins };
  }

  // Not decided — knock out the losing side and keep the match live.
  const updates = {};
  eliminatedSeats.forEach((i) => { updates[`players/${i}/eliminated`] = true; });
  const chairVacated = eliminatedSeats.includes(match.activePlayer);

  // ── THE CHAIR ─────────────────────────────────────────────────────
  // If the seat holding the chair is one of the ones leaving, the next
  // seat is about to play A FULL HALF-TURN. That is a half-turn
  // transition and gets the half-turn transition's whole sequence, from
  // the one function that owns it — not four lines that write
  // `activePlayer` and stop.
  //
  // ⚠ ORDER: the elimination flags go down FIRST, alone, so the
  // hand-off reads a board on which the knocked-out seats are already
  // out. Handing off first and eliminating after would let the hand-off
  // seat the chair on a seat that is about to be removed.
  //
  // ⚠ skipProphecies, ALWAYS. This function is itself called from
  // inside checkProphecies' resolution of a Prophecy; asking for
  // Prophecies again from here would re-enter the check that is still
  // running. The next ordinary hand-off asks — and R33's ruling in
  // turnEngine's banner already says a win already decided beats a
  // Prophecy completed in the same window.
  if (!chairVacated) {
    await updateMatch(code, updates);
    return { ended: false, winner: null, winners: [], runnerUp: null, eliminatedSeats };
  }

  const nextActive = getNextActiveSeat(projected, match.activePlayer);
  if (typeof _handOffHalfTurn === "function") {
    await updateMatch(code, updates);
    const res = await _handOffHalfTurn(code, projected, match.activePlayer, {
      nextIdx: nextActive, skipProphecies: true,
    });
    // The clock can be called BY this reseat — it is a real half-turn
    // and R93 counts real half-turns. When it is, the duel is over and
    // this function has to say so or its caller will narrate a knockout
    // on a finished match.
    if (res && res.ended) {
      const clockWinner = res.verdict ? res.verdict.winner : null;
      // ⚠ `res.winners`, NOT `[clockWinner]`. The hand-off had just
      // assembled the full R98 list — endOnClock resolves co-winners on
      // the clock path and returns them — and this line threw it away
      // and rebuilt a one-element array from the board winner. The
      // STORED state was right (endMatchRaw wrote `winners` itself), so
      // nothing about the archived duel was wrong; what was wrong was
      // the value handed back to prophecyEngine, which does
      // `winners: outcome.winners || …`, and to the renderer, which
      // narrates it. A co-winner was recorded and then told they lost.
      const winners = Array.isArray(res.winners) && res.winners.length
        ? res.winners
        : (clockWinner != null ? [clockWinner] : []);
      return { ended: true, winner: clockWinner, winners,
               runnerUp: res.verdict ? res.verdict.runnerUp : null, eliminatedSeats };
    }
    return { ended: false, winner: null, winners: [], runnerUp: null, eliminatedSeats };
  }

  // FALLBACK — turnEngine was never loaded. The chair still has to
  // move; it just moves without the clock, exactly as it always did.
  updates.activePlayer = nextActive;
  await updateMatch(code, updates);
  return { ended: false, winner: null, winners: [], runnerUp: null, eliminatedSeats };
}

const matchOutcome = { finishOrEliminateSeat, setHandOffHalfTurn, setCoWinnerResolver };
export default matchOutcome;
